import React, { useState } from "react";
import { api, formatApiError, fmtINR } from "../lib/apiClient";
import { UNITS, DOC_TYPES } from "../lib/constants";
import { X, Plus, Trash, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Modal: convert a source document (SO / DC / RI) → Tax Invoice.
 * User can review, edit, add or remove line items + transport notes before creating.
 */
let _convertSeq = 0;
const newConvertUid = () => `cv-${++_convertSeq}-${Date.now()}`;

export default function ConvertToInvoiceModal({ source, target = "invoice", onClose, onConverted }) {
  const [items, setItems] = useState(
    (source.items || []).map(it => ({
      _uid: newConvertUid(),
      product_id: it.product_id || "",
      name: it.name || "",
      description: it.description || "",
      hsn: it.hsn || "",
      unit: it.unit || "NOS",
      quantity: Number(it.quantity || 0),
      rate: Number(it.rate || 0),
      discount_pct: Number(it.discount_pct || 0),
      gst_rate: Number(it.gst_rate || 0),
    }))
  );
  const [notes, setNotes] = useState(source.notes || "");
  const [terms, setTerms] = useState(source.terms || "");
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const setItem = (i, patch) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addRow = () => setItems(prev => [...prev, { _uid: newConvertUid(), product_id: "", name: "", description: "", hsn: "", unit: "NOS", quantity: 1, rate: 0, discount_pct: 0, gst_rate: 18 }]);
  const removeRow = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const totalPreview = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.rate || 0) * (1 - Number(it.discount_pct || 0) / 100) * (1 + Number(it.gst_rate || 0) / 100), 0);

  const confirm = async () => {
    if (items.length === 0 || items.every(i => !i.name)) return toast.error("At least one line item is required");
    setSaving(true);
    try {
      const { data } = await api.post(`/documents/${source.id}/convert`, {
        target,
        edits: {
          doc_date: docDate,
          notes,
          terms,
          items: items.filter(i => i.name).map(i => ({
            ...i,
            quantity: Number(i.quantity || 0),
            rate: Number(i.rate || 0),
            discount_pct: Number(i.discount_pct || 0),
            gst_rate: Number(i.gst_rate || 0),
          })),
        },
      });
      toast.success(`${DOC_TYPES[target].label} created · ${data.doc_number}`);
      onConverted?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const tgtLabel = DOC_TYPES[target].label;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="convert-modal">
      <div className="bg-white rounded-lg border border-border max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="flex items-center gap-2 font-display text-lg font-semibold"><Receipt size={18} weight="duotone" className="text-brand" /> Generate {tgtLabel}</div>
            <div className="text-xs text-ink-muted mt-0.5">
              From <span className="font-mono font-semibold">{source.doc_number}</span> · {source.party_snapshot?.name} · Review and edit line items before creating the {tgtLabel.toLowerCase()}.
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="close-convert-modal"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><div className="label-overline block mb-1.5">{tgtLabel} Date</div><input type="date" className="field" value={docDate} onChange={e => setDocDate(e.target.value)} data-testid="convert-date" /></div>
          </div>

          <div className="card-base overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <div className="font-display font-semibold text-sm">Line Items</div>
              <button onClick={addRow} className="btn-secondary inline-flex items-center gap-1 text-xs" data-testid="convert-add-row"><Plus size={12} weight="bold" /> Add Row</button>
            </div>
            <div className="overflow-x-auto">
              <table className="table-base min-w-[900px]">
                <thead><tr>
                  <th className="w-8">#</th>
                  <th>Item</th>
                  <th>HSN</th>
                  <th>Unit</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Rate</th>
                  <th className="text-right">Disc%</th>
                  <th className="text-right">GST%</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it._uid || `cv-row-${i}`} data-testid={`convert-row-${i}`}>
                      <td className="text-ink-muted">{i + 1}</td>
                      <td><input className="field" value={it.name} onChange={e => setItem(i, { name: e.target.value })} data-testid={`convert-name-${i}`} /></td>
                      <td><input className="field font-mono" style={{ width: 90 }} value={it.hsn} onChange={e => setItem(i, { hsn: e.target.value })} /></td>
                      <td>
                        <select className="field" style={{ width: 80 }} value={it.unit} onChange={e => setItem(i, { unit: e.target.value })}>
                          {UNITS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="text-right"><input type="number" step="1" min="0" className="field text-right" style={{ width: 80 }} value={it.quantity} onChange={e => setItem(i, { quantity: e.target.value })} data-testid={`convert-qty-${i}`} /></td>
                      <td className="text-right"><input type="number" step="0.01" className="field text-right" style={{ width: 90 }} value={it.rate} onChange={e => setItem(i, { rate: e.target.value })} /></td>
                      <td className="text-right"><input type="number" step="0.01" className="field text-right" style={{ width: 70 }} value={it.discount_pct} onChange={e => setItem(i, { discount_pct: e.target.value })} /></td>
                      <td className="text-right">
                        <select className="field text-right" style={{ width: 70 }} value={it.gst_rate} onChange={e => setItem(i, { gst_rate: Number(e.target.value) })}>
                          {[0, 0.25, 3, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                        </select>
                      </td>
                      <td><button onClick={() => removeRow(i)} className="btn-ghost text-destructive"><Trash size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><div className="label-overline block mb-1.5">Notes (optional)</div><textarea rows={3} className="field" value={notes} onChange={e => setNotes(e.target.value)} data-testid="convert-notes" /></div>
            <div><div className="label-overline block mb-1.5">Terms (optional)</div><textarea rows={3} className="field" value={terms} onChange={e => setTerms(e.target.value)} /></div>
          </div>

          <div className="flex items-center justify-end gap-3 px-1 text-sm">
            <span className="text-ink-muted">Approx total (incl. tax):</span>
            <span className="font-display text-lg font-bold tabular text-brand" data-testid="convert-preview-total">{fmtINR(totalPreview)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-border bg-surface-muted">
          <div className="text-xs text-ink-muted">Source will remain unchanged; a new {tgtLabel.toLowerCase()} will be created with a fresh number.</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-secondary" data-testid="convert-cancel">Cancel</button>
            <button onClick={confirm} disabled={saving} className="btn-primary inline-flex items-center gap-1" data-testid="convert-confirm">
              <Receipt size={14} weight="bold" /> {saving ? "Creating…" : `Create ${tgtLabel}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
